import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import HowToUseSection from '@/components/HowToUseSection';
import { HELP_URLS } from '@/components/HelpBenefits';
import WhatsNewSection from '@/components/WhatsNewSection';
import { ArrowLeft, TrendingUp, ChevronDown, ChevronUp, BookOpen, GitBranch, DollarSign, Calendar, Target, LineChart, Activity, Search } from 'lucide-react';

export default function GrowthPrognosisBenefits() {
  const navigate = useNavigate();
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(true);

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/growth-prognosis')}
          className="mb-6 text-[#0891b2] hover:text-[#0e7490] hover:bg-[#0891b2]/10"
          data-testid="back-to-growth-prognosis"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Growth Prognosis
        </Button>

        {/* Main Content Card */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-lg sm:text-2xl flex items-center gap-2 sm:gap-3">
              <TrendingUp className="h-5 w-5 sm:h-7 sm:w-7" />
              Benefits of Growth Prognosis
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6 space-y-4">
            
            <WhatsNewSection />

            {/* Learn the Benefits Section - Collapsible */}
            <Collapsible open={isBenefitsOpen} onOpenChange={setIsBenefitsOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg hover:bg-[#0891b2]/10 transition-colors"
                  data-testid="benefits-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0891b2]" />
                    </div>
                    <span className="font-semibold text-[#0891b2] text-sm sm:text-base">Learn the Benefits of Growth Prognosis</span>
                  </div>
                  {isBenefitsOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#0891b2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#0891b2]" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Introduction */}
                <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    Growth Prognosis enables producers to forecast future growth and feed requirements with confidence, using biologically grounded SGR and SFR models combined with real temperature profiles and mortality assumptions. As the production cycle progresses, sample weights can be fed back into the forecast to produce a live, sample-adjusted projection alongside the original model prediction. Integrated Missing Fish Intelligence detects potential unreported fish loss, adding a corrected forecast line to separate genuine biological growth from survivorship bias. The result is a forward-looking, data-driven view that evolves with actual in-cage performance.
                  </p>
                </section>

                {/* Benefit 1: Scenario Planning */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-full flex-shrink-0">
                      <GitBranch className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-amber-800 mb-2">Scenario Planning</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        A key benefit is scenario planning. By allowing users to adjust model performance and feeding levels, Growth Prognosis makes it possible to explore "what-if" situations—such as improved biological performance, constrained feeding, or different mortality assumptions—without risking real production outcomes. This supports proactive decision-making instead of reactive correction.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Multiple scenarios can be compared side-by-side, enabling managers to evaluate trade-offs between different feeding strategies, assess risk under varying conditions, and develop contingency plans before challenges arise in the field.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 2: Feed Budgeting */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-full flex-shrink-0">
                      <DollarSign className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-green-800 mb-2">Feed Budgeting and Cost Control</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        Growth Prognosis also improves feed budgeting and cost control. By calculating daily feed requirements and total feed needed to reach a target weight, it helps farms anticipate feed demand accurately, avoid over-ordering or shortages, and understand how feeding strategy impacts biological FCR and overall efficiency.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        This precise forecasting translates directly into better cash flow management, reduced waste from expired feed inventory, and improved negotiations with feed suppliers through more accurate volume commitments.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 3: Planning Accuracy */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
                      <Calendar className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-2">Enhanced Planning Accuracy</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        From a production management perspective, the tool enhances planning accuracy. It clarifies how long it should take to reach a target weight, what biomass will be present at different points in time, and how mortality affects final outcomes. This is critical for harvest planning, capacity management, and coordination across sites.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        With reliable growth projections, operations can better align harvest schedules with market demand, optimise processing facility utilisation, and coordinate logistics well in advance.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 4: Live Forecast Adjustment */}
                <section className="bg-purple-50 border border-purple-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-full flex-shrink-0">
                      <Activity className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-purple-800 mb-2">Live Forecast Adjustment with Sample Data</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        As real production unfolds, sample weights can be entered directly into the prognosis. The system re-anchors the forecast from the latest sample measurement and projects forward using the model's SGR, producing a Sample-Updated Forecast that reflects actual in-cage performance rather than the original model assumption alone.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        A dedicated set of revised KPIs appears alongside the original forecast, showing updated days to target, total feed, final biomass, and survivor count. Delta indicators highlight the difference—for example, if fish are growing faster than the model predicted, the revised days to target will be shorter and total feed lower, giving managers immediate visibility into how the cycle is tracking against plan.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        The daily data table also supports switching between the Original Forecast and Sample-Updated Forecast views, allowing detailed comparison of weight, feed, biomass, and FCR at any point in the cycle.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 5: Three-Line Chart Visualisation */}
                <section className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-cyan-100 rounded-full flex-shrink-0">
                      <LineChart className="h-4 w-4 text-cyan-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-cyan-800 mb-2">Three-Line Chart Visualisation</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        The growth chart dynamically renders up to three distinct forecast lines, each representing a different layer of insight:
                      </p>
                      <ul className="text-xs sm:text-sm text-gray-700 leading-relaxed space-y-1 ml-4 list-disc mb-2">
                        <li><span className="font-medium text-[#0891b2]">Original Forecast</span> — the pure model prediction based on initial parameters, always visible as the baseline.</li>
                        <li><span className="font-medium text-[#8b5cf6]">Sample-Updated Forecast</span> — appears as soon as sample checkpoints are entered, showing the re-anchored trajectory with actual weights and a forward projection.</li>
                        <li><span className="font-medium text-[#ef4444]">Corrected Forecast</span> — appears only when Missing Fish Intelligence confirms fish loss, showing the trajectory adjusted for the survivorship bias caused by unreported mortality.</li>
                      </ul>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        This layered view lets producers see at a glance how actual performance compares to the model and whether an apparent growth improvement is genuine biological gain or an artefact of fish loss.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 6: Integrated Missing Fish Intelligence */}
                <section className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
                      <Search className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-red-800 mb-2">Integrated Missing Fish Intelligence</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        Growth Prognosis includes built-in Missing Fish Intelligence powered by jump analysis. When sample checkpoints are entered, the system can detect unexplained upward jumps in average weight that may indicate unreported mortality or escape events. Unlike simple threshold-based alerts, the detection uses biological credibility modifiers—accounting for factors like stress recovery patterns, prior trajectory behaviour, and model-relative baselines—to score confidence without over-rejecting genuine biological events.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        The jump detection now uses a gap-weighted blend of trajectory projection and the adjusted model baseline, producing conservative estimates with full ranges for sparse datasets. When sample gaps are long, more weight is given to the model baseline to avoid overestimating losses from unreliable linear extrapolation.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        When fish loss is confirmed, the Corrected Forecast line removes the artificial weight boost caused by survivorship bias, giving a clearer picture of true biological growth. This distinction is essential for accurate feed planning, realistic biomass estimation, and early intervention when stock integrity is compromised.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Conclusion: Strategic Planning Tool */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full flex-shrink-0">
                      <Target className="h-4 w-4 text-[#0891b2]" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-[#0891b2] mb-2">Strategic Planning Tool</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2">
                        Ultimately, Growth Prognosis transforms growth forecasting into a strategic planning tool—aligning biological potential, feeding strategy, and operational constraints to support predictable performance, improved margins, and better long-term production control.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        By combining scenario modelling, live sample-driven forecast updates, three-line visual comparison, and integrated missing fish intelligence, it gives producers a complete picture of where a production cycle stands, where it is heading, and whether intervention is needed—all within a single dashboard.
                      </p>
                    </div>
                  </div>
                </section>
              </CollapsibleContent>
            </Collapsible>

            {/* How to Use Section - Standardized Component */}
            <HowToUseSection 
              featureName="Growth Prognosis"
              youtubeUrl={HELP_URLS.growthPrognosis.youtube}
            />

            {/* CTA */}
            <div className="pt-4 border-t border-slate-200 flex justify-center">
              <Button
                onClick={() => navigate('/growth-prognosis')}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base"
                data-testid="start-growth-prognosis-btn"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Start Growth Prognosis
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
